import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';

interface PatternFormData {
  patternText: string;
  patternType: string;
  classificationTarget: string;
  weight: number;
  description: string;
}

interface PatternFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PatternFormData) => Promise<void>;
  editPattern?: {
    dbId: number;
    patternText: string;
    patternType: string;
    classificationTarget: string;
    weight: number | null;
    description: string | null;
  } | null;
  loading?: boolean;
  error?: string | null;
}

const PATTERN_TYPES = [
  { value: 'keyword_exact', label: 'Exact Match' },
  { value: 'keyword_contains', label: 'Contains' },
  { value: 'regex', label: 'Regex' },
];

const CLASSIFICATION_TARGETS = [
  { value: 'software', label: 'Software' },
  { value: 'services', label: 'Services' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'agency', label: 'Agency' },
  { value: 'non_software', label: 'Non-Software' },
];

const PatternFormDialog = ({
  open,
  onClose,
  onSave,
  editPattern,
  loading = false,
  error = null,
}: PatternFormDialogProps) => {
  const [formData, setFormData] = useState<PatternFormData>({
    patternText: '',
    patternType: 'keyword_contains',
    classificationTarget: 'software',
    weight: 1.0,
    description: '',
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (editPattern) {
      setFormData({
        patternText: editPattern.patternText,
        patternType: editPattern.patternType,
        classificationTarget: editPattern.classificationTarget,
        weight: editPattern.weight ?? 1.0,
        description: editPattern.description ?? '',
      });
    } else {
      setFormData({
        patternText: '',
        patternType: 'keyword_contains',
        classificationTarget: 'software',
        weight: 1.0,
        description: '',
      });
    }
    setValidationError(null);
  }, [editPattern, open]);

  const handleChange = (field: keyof PatternFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.patternText.trim()) {
      setValidationError('Pattern text is required');
      return false;
    }

    if (formData.patternType === 'regex') {
      try {
        new RegExp(formData.patternText);
      } catch (e) {
        setValidationError('Invalid regex pattern');
        return false;
      }
    }

    if (formData.weight < 0 || formData.weight > 10) {
      setValidationError('Weight must be between 0 and 10');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    await onSave(formData);
  };

  const isEdit = !!editPattern;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Pattern' : 'Add New Pattern'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {(error || validationError) && <Alert severity="error">{error || validationError}</Alert>}

          <TextField
            label="Pattern Text"
            value={formData.patternText}
            onChange={e => handleChange('patternText', e.target.value)}
            fullWidth
            required
            disabled={isEdit}
            helperText={
              formData.patternType === 'regex'
                ? 'Enter a valid regex pattern'
                : formData.patternType === 'keyword_exact'
                  ? 'Exact match (case-insensitive)'
                  : 'Substring match (case-insensitive)'
            }
          />

          <FormControl fullWidth disabled={isEdit}>
            <InputLabel>Pattern Type</InputLabel>
            <Select
              value={formData.patternType}
              label="Pattern Type"
              onChange={e => handleChange('patternType', e.target.value)}
            >
              {PATTERN_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={isEdit}>
            <InputLabel>Classification Target</InputLabel>
            <Select
              value={formData.classificationTarget}
              label="Classification Target"
              onChange={e => handleChange('classificationTarget', e.target.value)}
            >
              {CLASSIFICATION_TARGETS.map(target => (
                <MenuItem key={target.value} value={target.value}>
                  {target.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Weight"
            type="number"
            value={formData.weight}
            onChange={e => handleChange('weight', parseFloat(e.target.value) || 0)}
            fullWidth
            inputProps={{ min: 0, max: 10, step: 0.1 }}
            helperText="Higher weight = stronger signal (0-10)"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={e => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={2}
            helperText="Optional: Explain why this pattern is useful"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatternFormDialog;
